import React, { FC, useState } from "react";
import { useCookies } from "react-cookie";
import style from "./style.module.css";

import {
  PosSettings,
  DEFAULT_POS_SETTINGS,
  getPosSettings,
  savePosSettings,
  resetPosSettings,
} from "../../utils/posSettings";
import { playNewOrderChime, playNewMessageChime } from "../../utils/soundAlerts";

const SettingsPage: FC = () => {
  const [cookies] = useCookies(["auth"]);
  const auth = cookies.auth || {};
  const [settings, setSettings] = useState<PosSettings>(getPosSettings);
  const [saved, setSaved] = useState(false);

  const update = <K extends keyof PosSettings>(key: K, value: PosSettings[K]) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleSave = () => {
    savePosSettings(settings);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleReset = () => {
    resetPosSettings();
    setSettings({ ...DEFAULT_POS_SETTINGS });
    setSaved(false);
  };

  return (
    <div className={style.wrapper}>
      <div className={style.pageHeader}>
        <h1 className={style.title}>Settings</h1>
        <p className={style.subtitle}>Customize your POS experience and display preferences</p>
      </div>

      <div className={style.grid}>
        {/* Display preferences */}
        <div className={style.card}>
          <div className={style.cardHeader}>
            <span className={style.cardIcon}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
            </span>
            Display
          </div>
          <div className={style.cardBody}>
            <div className={style.settingRow}>
              <div className={style.settingInfo}>
                <span className={style.settingLabel}>Items per page</span>
                <span className={style.settingDesc}>Number of rows shown in data tables</span>
              </div>
              <select
                className={style.select}
                value={settings.itemsPerPage}
                onChange={e => update("itemsPerPage", Number(e.target.value))}
              >
                {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>

            <div className={style.settingRow}>
              <div className={style.settingInfo}>
                <span className={style.settingLabel}>Date format</span>
                <span className={style.settingDesc}>How dates are displayed across the app</span>
              </div>
              <select
                className={style.select}
                value={settings.dateFormat}
                onChange={e => update("dateFormat", e.target.value as "relative" | "absolute")}
              >
                <option value="absolute">Absolute (Jan 1, 2025)</option>
                <option value="relative">Relative (2 days ago)</option>
              </select>
            </div>

            <div className={style.settingRow}>
              <div className={style.settingInfo}>
                <span className={style.settingLabel}>Currency symbol</span>
                <span className={style.settingDesc}>Displayed next to prices</span>
              </div>
              <select
                className={style.select}
                value={settings.currencySymbol}
                onChange={e => update("currencySymbol", e.target.value)}
              >
                <option value="PKR">PKR — Pakistani Rupee</option>
                <option value="USD">USD — US Dollar</option>
                <option value="EUR">EUR — Euro</option>
                <option value="GBP">GBP — British Pound</option>
                <option value="AED">AED — UAE Dirham</option>
                <option value="SAR">SAR — Saudi Riyal</option>
              </select>
            </div>

            <div className={style.settingRow}>
              <div className={style.settingInfo}>
                <span className={style.settingLabel}>Receipt store name</span>
                <span className={style.settingDesc}>Custom name printed at the top of receipts (leave blank to use default store name)</span>
              </div>
              <input
                type="text"
                className={style.input}
                placeholder="e.g. My Store"
                value={settings.storeName || ""}
                onChange={e => update("storeName", e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Inventory preferences */}
        <div className={style.card}>
          <div className={style.cardHeader}>
            <span className={style.cardIcon}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
            </span>
            Inventory
          </div>
          <div className={style.cardBody}>
            <div className={style.settingRow}>
              <div className={style.settingInfo}>
                <span className={style.settingLabel}>Default low stock threshold</span>
                <span className={style.settingDesc}>Products below this quantity appear in Low Stock page</span>
              </div>
              <select
                className={style.select}
                value={settings.lowStockDefault}
                onChange={e => update("lowStockDefault", Number(e.target.value))}
              >
                {[0, 5, 10, 20, 50].map(n => <option key={n} value={n}>{n} units</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Audio & Notifications */}
        <div className={style.card}>
          <div className={style.cardHeader}>
            <span className={style.cardIcon}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
            </span>
            Notifications & Sound
          </div>
          <div className={style.cardBody}>
            <div className={style.settingRow}>
              <div className={style.settingInfo}>
                <span className={style.settingLabel}>Sound alerts</span>
                <span className={style.settingDesc}>Play audio chime when new orders or chat messages arrive</span>
              </div>
              <select
                className={style.select}
                value={settings.soundAlertsEnabled ? "enabled" : "disabled"}
                onChange={e => update("soundAlertsEnabled", e.target.value === "enabled")}
              >
                <option value="enabled">Enabled</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>

            <div className={style.settingRow}>
              <div className={style.settingInfo}>
                <span className={style.settingLabel}>Test audio chime</span>
                <span className={style.settingDesc}>Verify browser speaker output</span>
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  type="button"
                  style={{
                    padding: "6px 12px",
                    borderRadius: "6px",
                    border: "1px solid #d1d5db",
                    background: "#f9fafb",
                    fontSize: "12px",
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                  onClick={() => playNewOrderChime(true)}
                >
                  🔔 Order Chime
                </button>
                <button
                  type="button"
                  style={{
                    padding: "6px 12px",
                    borderRadius: "6px",
                    border: "1px solid #d1d5db",
                    background: "#f9fafb",
                    fontSize: "12px",
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                  onClick={() => playNewMessageChime(true)}
                >
                  💬 Message Ping
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* System info */}
        <div className={style.card}>
          <div className={style.cardHeader}>
            <span className={style.cardIcon}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            </span>
            System Information
          </div>
          <div className={style.cardBody}>
            <div className={style.infoGrid}>
              <div className={style.infoRow}>
                <span className={style.infoKey}>Backend URL</span>
                <span className={style.infoVal}>http://localhost:5500</span>
              </div>
              <div className={style.infoRow}>
                <span className={style.infoKey}>Logged in as</span>
                <span className={style.infoVal}>{auth.email || "—"}</span>
              </div>
              <div className={style.infoRow}>
                <span className={style.infoKey}>User ID</span>
                <span className={style.infoVal}>#{auth.user_id || "—"}</span>
              </div>
              <div className={style.infoRow}>
                <span className={style.infoKey}>Role</span>
                <span className={style.infoVal}>{auth.user_type || "—"}</span>
              </div>
              <div className={style.infoRow}>
                <span className={style.infoKey}>App version</span>
                <span className={style.infoVal}>1.0.0</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Save bar */}
      <div className={style.saveBar}>
        <button className={style.resetBtn} onClick={handleReset}>Reset to Defaults</button>
        <button className={`${style.saveBtn} ${saved ? style.saveBtnDone : ""}`} onClick={handleSave}>
          {saved ? "Saved!" : "Save Preferences"}
        </button>
      </div>
    </div>
  );
};

export default SettingsPage;
